import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../../../utils";
import { GetAllGGNotificationDTO, GetAllGGNotificationFiltersDTO, GGNotificationDTO, GGNotificationUpdateDTO } from "./dto";
import { NewGGNotificationService } from "./service";

interface GGNotificationState {
    // REQUEST
    ggNotificationCreationRequest: GGNotificationUpdateDTO
    ggNotificationUpdateRequest: GGNotificationUpdateDTO
    ggNotificationIdToUpdate: string

    // FILTERS
    ggNotificationFilters: GetAllGGNotificationFiltersDTO

    // STATUS
    getAllGGNotificationStatus: PromiseStatuses
    getGGNotificationByIdStatus: PromiseStatuses
    ggNotificationUpdateStatus: PromiseStatuses
    ggNotificationCreationStatus: PromiseStatuses

    // RESPONSE
    getAllGGNotificationResponse?: GetAllGGNotificationDTO
    getGGNotificationByIdResponse?: GGNotificationDTO
}

const initialState: GGNotificationState = {
    ggNotificationCreationRequest: {},
    ggNotificationUpdateRequest: {},
    ggNotificationIdToUpdate: '',

    ggNotificationFilters: {
        itemsPerPage: 0,
        page: 0
    },

    getAllGGNotificationStatus: 'idle',
    getGGNotificationByIdStatus: 'idle',
    ggNotificationCreationStatus: 'idle',
    ggNotificationUpdateStatus: 'idle'
}

export const GetAllGGNotifications = createAsyncThunk(
    'GGNotification/GetAll',
    async (filters: GetAllGGNotificationFiltersDTO, thunkApi): Promise<GetAllGGNotificationDTO> => {
        const GGNotificationService = NewGGNotificationService()
        return GGNotificationService.GetAllGGNotification(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetGGNotificationById = createAsyncThunk(
    'GGNotification/GetById',
    async (id: string, thunkApi): Promise<GGNotificationDTO> => {
        const GGNotificationService = NewGGNotificationService()
        return GGNotificationService.GetGGNotificationById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GGNotificationUpdate = createAsyncThunk(
    'GGNotification/Update',
    async (request: { body: GGNotificationUpdateDTO, id: string }, thunkApi): Promise<void> => {
        const GGNotificationService = NewGGNotificationService()
        return GGNotificationService.UpdateGGNotification(request.body, request.id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GGNotificationCreation = createAsyncThunk(
    'GGNotification/Creation',
    async (data: GGNotificationUpdateDTO, thunkApi): Promise<void> => {
        const GGNotificationService = NewGGNotificationService()
        return GGNotificationService.CreateGGNotification(data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

const GGNotificationSlice = createSlice({
    name: 'ggNotification/slice',
    initialState,
    reducers: {
        // REQUEST [CREATION]
        setGGNotificationCreationCustomerId: (state, action) => {
            state.ggNotificationCreationRequest.customerId = action.payload
        },
        setGGNotificationCreationRequest: (state, action) => {
            state.ggNotificationCreationRequest = action.payload
        },
        setGGNotCreationEntryProtocolId: (state, action) => {
            state.ggNotificationCreationRequest.entryProtocolId = action.payload
        },
        setGGNotCreationType: (state, action) => {
            state.ggNotificationCreationRequest.type = action.payload
        },
        setGGNotCreationOtherType: (state, action) => {
            state.ggNotificationCreationRequest.otherType = action.payload
        },
        setGGNotCreationStandard: (state, action) => {
            state.ggNotificationCreationRequest.standard = action.payload
        },
        setGGNotCreationStandardSubfield: (state, action) => {
            state.ggNotificationCreationRequest.standardSubfield = action.payload
        },
        setGGNotCreationStandardOptions: (state, action) => {
            state.ggNotificationCreationRequest.standardOption = action.payload
        },
        setGGNotCreationGraspModule: (state, action) => {
            state.ggNotificationCreationRequest.graspModule = action.payload
        },
        setGGNotCreationCoopModule: (state, action) => {
            state.ggNotificationCreationRequest.coopModule = action.payload
        },
        setGGNotCreationSpringModule: (state, action) => {
            state.ggNotificationCreationRequest.springModule = action.payload
        },
        setGGNotCreationValutationDate: (state, action) => {
            state.ggNotificationCreationRequest.valutationDate = action.payload
        },
        setGGNotCreationValutationResult: (state, action) => {
            state.ggNotificationCreationRequest.valutationResult = action.payload
        },
        setGGNotCreationValutationNote: (state, action) => {
            state.ggNotificationCreationRequest.valutationNote = action.payload
        },
        setGGNotCreationManipulationSoldProducts: (state, action) => {
            state.ggNotificationCreationRequest.manipulationSoldProducts = action.payload
        },
        setGGNotCreationManipulationPostHarvesting: (state, action) => {
            state.ggNotificationCreationRequest.manipulationPostHarvesting = action.payload
        },
        setGGNotCreationManipulationInnerStructures: (state, action) => {
            state.ggNotificationCreationRequest.manipualtionInnerStructures = action.payload
        },
        setGGNotCreationManipulationOtherStructuresNoGG: (state, action) => {
            state.ggNotificationCreationRequest.manipulationOtherStructuresNoGG = action.payload
        },
        setGGNotCreationManipulationOtherStructuresGGWithGGN: (state, action) => {
            state.ggNotificationCreationRequest.manipulationOtherStructuresGGWithGGN = action.payload
        },
        setGGNotCreationManipulationExtraFarmProducts: (state, action) => {
            state.ggNotificationCreationRequest.manipulationExtraFarmProducts = action.payload
        },
        resetGGNotificationCreationReuqest: (state) => {
            state.ggNotificationCreationRequest = {}
        },

        // REQUEST [UPDATE]
        setGGNotificationUpdateRequest: (state, action) => {
            state.ggNotificationUpdateRequest = action.payload
        },
        setGGNotificationIdToUpdate: (state, action) => {
            state.ggNotificationIdToUpdate = action.payload
        },
        setGGNotUpdateEntryProtocolId: (state, action) => {
            state.ggNotificationUpdateRequest.entryProtocolId = action.payload
        },
        setGGNotUpdateType: (state, action) => {
            state.ggNotificationUpdateRequest.type = action.payload
        },
        setGGNotUpdateOtherType: (state, action) => {
            state.ggNotificationUpdateRequest.otherType = action.payload
        },
        setGGNotUpdateStandard: (state, action) => {
            state.ggNotificationUpdateRequest.standard = action.payload
        },
        setGGNotUpdateStandardSubfield: (state, action) => {
            state.ggNotificationUpdateRequest.standardSubfield = action.payload
        },
        setGGNotUpdateStandardOptions: (state, action) => {
            state.ggNotificationUpdateRequest.standardOption = action.payload
        },
        setGGNotUpdateGraspModule: (state, action) => {
            state.ggNotificationUpdateRequest.graspModule = action.payload
        },
        setGGNotUpdateCoopModule: (state, action) => {
            state.ggNotificationUpdateRequest.coopModule = action.payload
        },
        setGGNotUpdateSpringModule: (state, action) => {
            state.ggNotificationUpdateRequest.springModule = action.payload
        },
        setGGNotUpdateValutationDate: (state, action) => {
            state.ggNotificationUpdateRequest.valutationDate = action.payload
        },
        setGGNotUpdateValutationResult: (state, action) => {
            state.ggNotificationUpdateRequest.valutationResult = action.payload
        },
        setGGNotUpdateValutationNote: (state, action) => {
            state.ggNotificationUpdateRequest.valutationNote = action.payload
        },
        setGGNotUpdateManipulationSoldProducts: (state, action) => {
            state.ggNotificationUpdateRequest.manipulationSoldProducts = action.payload
        },
        setGGNotUpdateManipulationPostHarvesting: (state, action) => {
            state.ggNotificationUpdateRequest.manipulationPostHarvesting = action.payload
        },
        setGGNotUpdateManipulationInnerStructures: (state, action) => {
            state.ggNotificationUpdateRequest.manipualtionInnerStructures = action.payload
        },
        setGGNotUpdateManipulationOtherStructuresNoGG: (state, action) => {
            state.ggNotificationUpdateRequest.manipulationOtherStructuresNoGG = action.payload
        },
        setGGNotUpdateManipulationOtherStructuresGGWithGGN: (state, action) => {
            state.ggNotificationUpdateRequest.manipulationOtherStructuresGGWithGGN = action.payload
        },
        setGGNotUpdateManipulationExtraFarmProducts: (state, action) => {
            state.ggNotificationUpdateRequest.manipulationExtraFarmProducts = action.payload
        },
        resetGGNotificationUpdateReuqest: (state) => {
            state.ggNotificationUpdateRequest = {}
        },

        // FILTERS
        setGGNotFiltersItemsPerPage: (state, action) => {
            state.ggNotificationFilters.itemsPerPage = action.payload
        },
        setGGNotFiltersPage: (state, action) => {
            state.ggNotificationFilters.page = action.payload
        },
        setGGNotFiltersCustomerId: (state, action) => {
            state.ggNotificationFilters.customerId = action.payload
        },
        resetGGNotificationFilters: (state) => {
            state.ggNotificationFilters = {
                itemsPerPage: 0,
                page: 0
            }
        },

        // STATUS
        resetGGNotificationCreationStatus: (state) => {
            state.ggNotificationCreationStatus = 'idle'
        },
        resetGetAllGGNotificationStatus: (state) => {
            state.getAllGGNotificationStatus = 'idle'
        },
        resetGetGGNotificationByIdStatus: (state) => {
            state.getGGNotificationByIdStatus = 'idle'
        },
        resetGGNotificationUpdateStatus: (state) => {
            state.ggNotificationUpdateStatus = 'idle'
        }
    },
    extraReducers(builder) {
        builder

            // GET ALL
            .addCase(GetAllGGNotifications.pending, (state) => {
                state.getAllGGNotificationStatus = 'loading'
            })
            .addCase(GetAllGGNotifications.fulfilled, (state, action) => {
                state.getAllGGNotificationStatus = 'successfully'
                state.getAllGGNotificationResponse = action.payload
            })
            .addCase(GetAllGGNotifications.rejected, (state, action) => {
                state.getAllGGNotificationStatus = 'failed'
            })

            // GET BY ID
            .addCase(GetGGNotificationById.pending, (state) => {
                state.getGGNotificationByIdStatus = 'loading'
            })
            .addCase(GetGGNotificationById.fulfilled, (state, action) => {
                state.getGGNotificationByIdStatus = 'successfully'
                state.getGGNotificationByIdResponse = action.payload
            })
            .addCase(GetGGNotificationById.rejected, (state, action) => {
                state.getGGNotificationByIdStatus = 'failed'
            })

            // UPDATE
            .addCase(GGNotificationUpdate.pending, (state) => {
                state.ggNotificationUpdateStatus = 'loading'
            })
            .addCase(GGNotificationUpdate.fulfilled, (state) => {
                state.ggNotificationUpdateStatus = 'successfully'
            })
            .addCase(GGNotificationUpdate.rejected, (state, action) => {
                state.ggNotificationUpdateStatus = 'failed'
            })

            // CREATION
            .addCase(GGNotificationCreation.pending, (state) => {
                state.ggNotificationCreationStatus = 'loading'
            })
            .addCase(GGNotificationCreation.fulfilled, (state) => {
                state.ggNotificationCreationStatus = 'successfully'
            })
            .addCase(GGNotificationCreation.rejected, (state, action) => {
                state.ggNotificationCreationStatus = 'failed'
            })
    },
})

export const {
    // REQUEST
    setGGNotificationCreationCustomerId,
    setGGNotificationUpdateRequest,
    setGGNotUpdateCoopModule,
    setGGNotUpdateEntryProtocolId,
    setGGNotUpdateGraspModule,
    setGGNotUpdateManipulationInnerStructures,
    setGGNotUpdateManipulationOtherStructuresGGWithGGN,
    setGGNotUpdateManipulationOtherStructuresNoGG,
    setGGNotUpdateManipulationPostHarvesting,
    setGGNotUpdateManipulationSoldProducts,
    setGGNotUpdateManipulationExtraFarmProducts,
    setGGNotUpdateOtherType,
    setGGNotUpdateSpringModule,
    setGGNotUpdateStandard,
    setGGNotUpdateStandardOptions,
    setGGNotUpdateStandardSubfield,
    setGGNotUpdateType,
    setGGNotUpdateValutationDate,
    setGGNotUpdateValutationNote,
    setGGNotUpdateValutationResult,
    setGGNotificationIdToUpdate,
    resetGGNotificationUpdateReuqest,
    setGGNotCreationCoopModule,
    setGGNotCreationEntryProtocolId,
    setGGNotCreationGraspModule,
    setGGNotCreationManipulationExtraFarmProducts,
    setGGNotCreationManipulationInnerStructures,
    setGGNotCreationManipulationOtherStructuresGGWithGGN,
    setGGNotCreationManipulationOtherStructuresNoGG,
    setGGNotCreationManipulationPostHarvesting,
    setGGNotCreationManipulationSoldProducts,
    setGGNotCreationOtherType,
    setGGNotCreationSpringModule,
    setGGNotCreationStandard,
    setGGNotCreationStandardOptions,
    setGGNotCreationStandardSubfield,
    setGGNotCreationType,
    setGGNotCreationValutationDate,
    setGGNotCreationValutationNote,
    setGGNotCreationValutationResult,
    setGGNotificationCreationRequest,
    resetGGNotificationCreationReuqest,

    // FILTERS
    setGGNotFiltersCustomerId,
    setGGNotFiltersItemsPerPage,
    setGGNotFiltersPage,
    resetGGNotificationFilters,

    // STATUS
    resetGGNotificationCreationStatus,
    resetGGNotificationUpdateStatus,
    resetGetAllGGNotificationStatus,
    resetGetGGNotificationByIdStatus
} = GGNotificationSlice.actions

export default GGNotificationSlice.reducer